/**
 * The title card.
 *
 * Five letters resolving out of a blur and one meter sweep under them, then it
 * gets out of the way. It plays once per browser session: a reload in the
 * middle of an edit should put you back in front of the photograph, not in
 * front of a logo.
 */

import { useEffect, useState } from 'react';

const SESSION_KEY = 'lumos.opening';
/** Time on screen before the fade starts. */
const HOLD_MS = 1900;
/** Matches the .opening.done fade in the stylesheet. */
const CLEAR_MS = 620;
/** The reduced-motion pass, where the CSS collapses every animation to nothing. */
const STILL_HOLD_MS = 420;
const STILL_CLEAR_MS = 40;

const WORDMARK = 'LUMOS';
const LETTER_STAGGER_MS = 55;

type Phase = 'title' | 'clearing' | 'done';

function alreadyPlayed(): boolean {
  try {
    return window.sessionStorage.getItem(SESSION_KEY) === 'played';
  } catch {
    return false;
  }
}

function remember(): void {
  try {
    window.sessionStorage.setItem(SESSION_KEY, 'played');
  } catch {
    // A browser with storage switched off replays the card. Nothing else breaks.
  }
}

function prefersStill(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export interface OpeningState {
  /** The card is mounted. */
  showing: boolean;
  /** The card is fading. */
  clearing: boolean;
  /** The shell can take over. */
  finished: boolean;
}

export function useOpening(): OpeningState {
  const [phase, setPhase] = useState<Phase>(() => (alreadyPlayed() ? 'done' : 'title'));

  useEffect(() => {
    if (phase === 'done') return;
    if (phase === 'title') remember();
    // Timers rather than animationend: under reduced motion the CSS runs the
    // animations for a millisecond, and a card that waited on events could sit
    // there over a finished animation if one never fired.
    const still = prefersStill();
    const wait = phase === 'title' ? (still ? STILL_HOLD_MS : HOLD_MS) : still ? STILL_CLEAR_MS : CLEAR_MS;
    const timer = window.setTimeout(() => setPhase(phase === 'title' ? 'clearing' : 'done'), wait);
    return () => window.clearTimeout(timer);
  }, [phase]);

  return { showing: phase !== 'done', clearing: phase === 'clearing', finished: phase === 'done' };
}

export function Opening({ clearing }: { clearing: boolean }) {
  return (
    <div className={`opening${clearing ? ' done' : ''}`} aria-hidden="true">
      <div className="opening-mark">
        <div className="opening-word">
          {WORDMARK.split('').map((letter, index) => (
            <span
              key={`${letter}-${index}`}
              style={{
                animationDelay: `${index * LETTER_STAGGER_MS}ms`,
                color: letter === 'O' ? 'var(--ember)' : undefined,
              }}
            >
              {letter}
            </span>
          ))}
        </div>
        <div className="opening-sweep" />
      </div>
    </div>
  );
}
